import React, { useContext, useEffect, useMemo, useState } from "react";
import { Button, Form, InputNumber, Modal, Select, message } from "antd";
import { EditOutlined, FileExcelOutlined } from "@ant-design/icons";
import { TableCell } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import ServerTabBox from "src/Components/Tabbox/ServerTabBox";
import { ServerTabsContext } from "src/Layout/MasterPage";
import TableTemplate, { IAdditional } from "src/Tables/TableTemplate/tableTemplate";
import FileDownloader from "src/Components/FileDownloader";
import { CommonTableUI } from "src/Tables/TableTemplate/common";
import Execution from "./Execution";
import {
  addDomain, deleteDomain, editDomain, getBaseChoices, getDomainBaseChoices, getDomainTable,
  getFuturesTable, getFuturesWorkbookExcel, getSettings, getSyntheticTable, saveSettings,
  BaseChoice, DomainRow, FuturesSearch, FuturesSettings,
} from "src/Requests/futuresMarketMaking";

const FuturesMarketMaking: React.FC = () => {
  const [searchParams] = useSearchParams({ activeTab: "MarketExecution" });
  const tabs = useContext(ServerTabsContext).futuresMarketMaking;
  const activeTab = searchParams.get("activeTab");
  const [choices, setChoices] = useState<BaseChoice[]>([]);
  const [domainChoices, setDomainChoices] = useState<BaseChoice[]>([]);
  const [settings, setSettings] = useState<FuturesSettings>({ annualInterestRatePercent: null });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [domainOpen, setDomainOpen] = useState(false);
  const [editing, setEditing] = useState<DomainRow | null>(null);
  const [domainId, setDomainId] = useState<string>();
  const [domainMaxSpread, setDomainMaxSpread] = useState<number | null>(null);
  const [domainMaxSpreadPercent, setDomainMaxSpreadPercent] = useState<number | null>(null);
  const [selectedBase, setSelectedBase] = useState<string>();
  const [selectedFuture, setSelectedFuture] = useState<string>();
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    getBaseChoices().then(setChoices).catch(() => message.error("دریافت نمادهای پایه ناموفق بود"));
    getSettings().then(setSettings).catch(() => message.error("دریافت تنظیمات ناموفق بود"));
  }, []);

  useEffect(() => {
    getDomainBaseChoices().then(items => {
      setDomainChoices(items);
      setSelectedBase(current => current && !items.some(x => x.instrumentId === current) ? undefined : current);
    }).catch(() => message.error("دریافت دامنه نمادهای پایه ناموفق بود"));
  }, [refresh]);

  useEffect(() => { setSelectedFuture(undefined); }, [selectedBase]);

  const updateSettings = async () => {
    try {
      await saveSettings(settings);
      setSettingsOpen(false);
      setRefresh(x => x + 1);
      message.success("تنظیمات ذخیره شد");
    } catch { message.error("ذخیره تنظیمات ناموفق بود"); }
  };

  const updateDomain = async () => {
    if (!domainId) return message.warning("نماد پایه را انتخاب کنید");
    try {
      const input = {
        baseInstrumentId: domainId,
        maxOptionSpread: domainMaxSpread,
        maxOptionSpreadPercent: domainMaxSpreadPercent,
      };
      if (editing) await editDomain(editing.id, input);
      else await addDomain(input);
      setDomainOpen(false);
      setEditing(null);
      setDomainId(undefined);
      setDomainMaxSpread(null);
      setDomainMaxSpreadPercent(null);
      setRefresh(x => x + 1);
      message.success("دامنه ذخیره شد");
    } catch { message.error("ذخیره دامنه ناموفق بود یا نماد تکراری است"); }
  };

  const removeDomain = async () => {
    if (!editing) return;
    try {
      await deleteDomain(editing.id);
      setDomainOpen(false);
      setEditing(null);
      setDomainId(undefined);
      setDomainMaxSpread(null);
      setDomainMaxSpreadPercent(null);
      setRefresh(x => x + 1);
      message.success("نماد از دامنه حذف شد");
    } catch { message.error("حذف نماد ناموفق بود"); }
  };

  const openDomainEditor = (row: DomainRow) => {
    setEditing(row);
    setDomainId(row.baseInstrumentId);
    setDomainMaxSpread(row.maxOptionSpread);
    setDomainMaxSpreadPercent(row.maxOptionSpreadPercent);
    setDomainOpen(true);
  };

  const domainActions: IAdditional<DomainRow> = {
    getHeader: () => <TableCell className="!text-center !font-semibold">عملیات</TableCell>,
    getRow: ({ row }) => <TableCell className="!text-center">
      <Button size="small" icon={<EditOutlined />} onClick={event => {
        event.stopPropagation();
        openDomainEditor(row);
      }}>ویرایش</Button>
    </TableCell>,
  };

  const domainSearch = useMemo<FuturesSearch>(() => ({}), []);
  const search = useMemo<FuturesSearch>(() => ({ baseInstrumentId: selectedBase }), [selectedBase]);
  const chainSearch = useMemo<FuturesSearch>(() => (
    { baseInstrumentId: selectedBase, instrumentId: selectedFuture }
  ), [selectedBase, selectedFuture]);

  return <div className="p-6 flex flex-col gap-5" dir="rtl">
    <h1 className="text-xl font-bold">بازارگردانی آتی</h1>
    <div className="w-full max-w-[360px]">
      <ServerTabBox children={tabs?.tabs}
        defaultTabLink="futuresMarketMaking?activeTab=MarketExecution" />
    </div>
    {activeTab === "Calculations" && <div className="flex flex-col gap-8">
      <div className="flex gap-3 items-center">
        <Button onClick={() => setSettingsOpen(true)}>تنظیمات</Button>
        <Select allowClear showSearch placeholder="فیلتر نماد پایه" style={{ minWidth: 190 }}
          value={selectedBase} onChange={value => { setSelectedBase(value); setSelectedFuture(undefined); }}
          options={domainChoices.map(x => ({ value: x.instrumentId, label: x.name }))} />
        <FileDownloader getFile={() => getFuturesWorkbookExcel({ futureSearch: search, chainSearch })}
          text={<Button icon={<FileExcelOutlined />}>خروجی اکسل محاسبات و زنجیره</Button>} />
      </div>
      <section><h2 className="font-semibold mb-2">محاسبه قیمت آتی</h2>
        <TableTemplate getDataTable={getFuturesTable} searchParameters={search} silentRefreshKey={refresh}
          refreshTableTime={30000} withSort TableUI={({ reports, sortingType, setSortingType }) =>
            <CommonTableUI reports={reports} sortingType={sortingType} setSortingType={setSortingType}
              activeRowId={reports.report.find(row => row.instrumentId === selectedFuture)?.id}
              activeRowClassName="!bg-slate-200 hover:!bg-slate-200"
              onTableRowClick={row => setSelectedFuture(row.instrumentId)} />} />
      </section>
      <section><h2 className="font-semibold mb-2">زنجیره سینتتیک آپشن</h2>
        {selectedFuture && <Button onClick={() => setSelectedFuture(undefined)}>نمایش همه سررسیدها</Button>}
        <TableTemplate getDataTable={getSyntheticTable} searchParameters={chainSearch} silentRefreshKey={refresh}
          refreshTableTime={30000} withSort TableUI={({ reports, sortingType, setSortingType }) =>
            <CommonTableUI reports={reports} sortingType={sortingType} setSortingType={setSortingType}
              filterCols={{ show: false, tableName: "futuresSynthetic", allowedPositions: [] }} />} />
      </section>
    </div>}
    {activeTab === "MarketExecution" && <Execution />}

    <Modal title="تنظیمات بازارگردانی آتی" open={settingsOpen} onOk={updateSettings} width={960}
      onCancel={() => setSettingsOpen(false)} okText="ذخیره" cancelText="انصراف">
      <div className="max-h-[70vh] overflow-y-auto" dir="rtl">
        <Form layout="vertical">
          <Form.Item label="% نرخ بهره سالانه"><InputNumber min={0} style={{ width: 160 }}
            value={settings.annualInterestRatePercent} onChange={value => setSettings(x => ({ ...x, annualInterestRatePercent: value }))} /></Form.Item>
        </Form>
        <section className="border-t pt-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-semibold">دامنه نماد پایه</h2>
            <div className="flex items-center gap-2">
              <Button type="primary" onClick={() => {
                setEditing(null);
                setDomainId(undefined);
                setDomainMaxSpread(null);
                setDomainMaxSpreadPercent(null);
                setDomainOpen(true);
              }}>افزودن نماد پایه</Button>
            </div>
          </div>
          <TableTemplate getDataTable={getDomainTable} searchParameters={domainSearch} silentRefreshKey={refresh}
            TableUI={({ reports, onAction, sortingType, setSortingType }) =>
              <CommonTableUI reports={reports} onAction={onAction} sortingType={sortingType}
                setSortingType={setSortingType} tableBoxClassName="max-h-[300px]"
                AdditionalBody={domainActions} />} withSort />
        </section>
      </div>
    </Modal>
    <Modal title={editing ? "ویرایش نماد پایه" : "افزودن نماد پایه"} open={domainOpen}
      onOk={updateDomain} onCancel={() => setDomainOpen(false)} okText="ذخیره" cancelText="انصراف"
      footer={(_, { OkBtn, CancelBtn }) => <div className="flex justify-between">
        {editing ? <Button danger onClick={removeDomain}>حذف</Button> : <span />}
        <div className="flex gap-2"><CancelBtn /><OkBtn /></div>
      </div>}>
      <Form layout="vertical" dir="rtl">
        <Form.Item label="نماد پایه">
          <Select showSearch style={{ width: 240 }} placeholder="نماد پایه" value={domainId}
            onChange={setDomainId} options={choices.map(x => ({ value: x.instrumentId, label: x.name }))} />
        </Form.Item>
        <Form.Item label="حداکثر اسپرد دو سر آپشن (ریال)">
          <InputNumber min={0} style={{ width: 160 }} value={domainMaxSpread} onChange={setDomainMaxSpread} />
        </Form.Item>
        <Form.Item label="حداکثر % اسپرد دو سر آپشن">
          <InputNumber min={0} style={{ width: 160 }} value={domainMaxSpreadPercent} onChange={setDomainMaxSpreadPercent} />
        </Form.Item>
      </Form>
    </Modal>
  </div>;
};

export default FuturesMarketMaking;
